import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Info, RotateCcw, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Tooltip } from '../ui/tooltip';
import { cn } from '../../lib/utils';
import type { DayOfWeekCode, PlanSlotInfo, StaffingRequirement, StaffingRequirementPosition } from './types';
import {
  DAY_CODE_LABELS,
  WEEK_DAY_CODES,
  slotColorClass,
  slotHours,
  sortSlots,
} from './planningUtils';

export const MAX_HEADCOUNT = 99;

type CellValues = { min: string; target: string };
type Values = Record<string, CellValues>;

const key = (slotId: string, day: string) => `${slotId}|${day}`;

/** Returns the parsed number, or null when the text is not an integer 0–99. Empty = 0. */
export function parseHeadcount(text: string): number | null {
  const t = text.trim();
  if (t === '') return 0;
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= 0 && n <= MAX_HEADCOUNT ? n : null;
}

export function validateCell(v: CellValues): { min?: string; target?: string } {
  const errors: { min?: string; target?: string } = {};
  const min = parseHeadcount(v.min);
  const target = parseHeadcount(v.target);
  if (min === null) errors.min = `Wpisz liczbę całkowitą od 0 do ${MAX_HEADCOUNT}`;
  if (target === null) errors.target = `Wpisz liczbę całkowitą od 0 do ${MAX_HEADCOUNT}`;
  if (min !== null && target !== null && target < min) {
    errors.target = 'Docelowo nie może być mniej niż minimum';
  }
  return errors;
}

function normalize(values: Values): string {
  return Object.keys(values)
    .sort()
    .map((k) => `${k}=${parseHeadcount(values[k].min) ?? values[k].min}/${parseHeadcount(values[k].target) ?? values[k].target}`)
    .join(';');
}

interface Props {
  position: StaffingRequirementPosition;
  slotById: Map<string, PlanSlotInfo>;
  requirements: StaffingRequirement[];
  canEdit: boolean;
  onSave: (
    positionId: string,
    items: Array<{ shiftTimeId: string; dayOfWeek: string; minHeadcount: number; targetHeadcount: number }>,
  ) => Promise<boolean>;
}

export const RequirementsPositionCard: React.FC<Props> = ({
  position,
  slotById,
  requirements,
  canEdit,
  onSave,
}) => {
  const rows = useMemo(() => {
    const ids = new Set(position.slotIds);
    requirements.forEach((r) => ids.add(r.shiftTimeId));
    return sortSlots(
      Array.from(ids)
        .map((id) => slotById.get(id))
        .filter((s): s is PlanSlotInfo => !!s),
    );
  }, [position.slotIds, requirements, slotById]);

  const initial = useMemo(() => {
    const v: Values = {};
    for (const slot of rows) {
      for (const day of WEEK_DAY_CODES) v[key(slot.id, day)] = { min: '0', target: '0' };
    }
    for (const r of requirements) {
      v[key(r.shiftTimeId, r.dayOfWeek)] = { min: String(r.minHeadcount), target: String(r.targetHeadcount) };
    }
    return v;
  }, [rows, requirements]);
  const initialSignature = normalize(initial);

  const [values, setValues] = useState<Values>(initial);
  const [saving, setSaving] = useState(false);

  // Reset when the saved data changes (e.g. after saving).
  useEffect(() => {
    setValues(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSignature]);

  const errors = useMemo(() => {
    const out: Record<string, { min?: string; target?: string }> = {};
    for (const k of Object.keys(values)) {
      const e = validateCell(values[k]);
      if (e.min || e.target) out[k] = e;
    }
    return out;
  }, [values]);

  const invalid = Object.keys(errors).length > 0;
  const dirty = normalize(values) !== initialSignature;

  const setCell = (k: string, field: 'min' | 'target', text: string) =>
    setValues((prev) => ({ ...prev, [k]: { ...prev[k], [field]: text } }));

  const copyFirstDay = (slot: PlanSlotInfo) => {
    const days = WEEK_DAY_CODES.filter((d) => slot.daysOfWeek.includes(d));
    const source = days[0] ?? 'MON';
    setValues((prev) => {
      const next = { ...prev };
      const src = prev[key(slot.id, source)];
      for (const d of days.length ? days : WEEK_DAY_CODES) next[key(slot.id, d)] = { ...src };
      return next;
    });
  };

  const handleSave = async () => {
    if (invalid || !dirty) return;
    const items: Array<{ shiftTimeId: string; dayOfWeek: string; minHeadcount: number; targetHeadcount: number }> = [];
    for (const slot of rows) {
      for (const day of WEEK_DAY_CODES) {
        const v = values[key(slot.id, day)];
        const min = parseHeadcount(v.min) ?? 0;
        const target = parseHeadcount(v.target) ?? 0;
        if (min > 0 || target > 0) {
          items.push({ shiftTimeId: slot.id, dayOfWeek: day, minHeadcount: min, targetHeadcount: target });
        }
      }
    }
    setSaving(true);
    await onSave(position.positionId, items);
    setSaving(false);
  };

  const unusedSlotIds = new Set(rows.filter((s) => !position.slotIds.includes(s.id)).map((s) => s.id));

  return (
    <Card data-testid={`req-card-${position.positionId}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{position.positionName}</CardTitle>
        <CardDescription>
          {position.employeeCount} {position.employeeCount === 1 ? 'osoba' : 'osób'} na stanowisku
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 ? (
          <div className="flex items-start gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
            <span>
              Osoby na tym stanowisku nie mają zmianowości, więc nie ma dla kogo ustawić obsady. Przypisz zmianowość
              pracownikom albo ustaw „Domyślną zmianowość” stanowiska (Kadry → Stanowiska).
            </span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 min-w-[150px] bg-card border-b px-2 py-1.5 text-left font-medium">
                      Zmiana
                    </th>
                    {WEEK_DAY_CODES.map((d) => (
                      <th key={d} className="min-w-[104px] border-b px-1 py-1.5 text-center font-medium">
                        <div>{DAY_CODE_LABELS[d].short}</div>
                        <div className="text-[10px] font-normal text-muted-foreground">min · cel</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((slot) => (
                    <tr key={slot.id}>
                      <th
                        scope="row"
                        className="sticky left-0 z-10 bg-card border-b px-2 py-2 text-left align-top font-normal"
                      >
                        <div className="flex items-center gap-1.5">
                          <span
                            className={cn(
                              'rounded border px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                              slotColorClass(slot.startTime),
                            )}
                          >
                            {slotHours(slot.startTime, slot.endTime)}
                          </span>
                          {canEdit && (
                            <Tooltip content="Powtórz wartości z pierwszego dnia tej zmiany na pozostałe jej dni" side="top">
                              <button
                                type="button"
                                aria-label="Powtórz wartości z pierwszego dnia"
                                onClick={() => copyFirstDay(slot)}
                                className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">{slot.shiftName}</div>
                        {unusedSlotIds.has(slot.id) && (
                          <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                            Nikt z tego stanowiska nie pracuje już na tej zmianie
                          </div>
                        )}
                      </th>
                      {WEEK_DAY_CODES.map((day: DayOfWeekCode) => {
                        const k = key(slot.id, day);
                        const v = values[k];
                        const err = errors[k];
                        const usual = slot.daysOfWeek.includes(day);
                        const label = `${slotHours(slot.startTime, slot.endTime)}, ${DAY_CODE_LABELS[day].long}`;
                        return (
                          <td
                            key={day}
                            className={cn('border-b px-1 py-2 align-top', !usual && 'bg-muted/40')}
                            title={usual ? undefined : 'Ta zmiana zwykle nie działa w ten dzień'}
                          >
                            <div className="flex justify-center gap-1">
                              <input
                                type="text"
                                inputMode="numeric"
                                aria-label={`Minimum: ${label}`}
                                data-testid={`req-${position.positionId}-${slot.id}-${day}-min`}
                                value={v.min}
                                disabled={!canEdit || saving}
                                onChange={(e) => setCell(k, 'min', e.target.value)}
                                aria-invalid={!!err?.min}
                                className={cn(
                                  'h-8 w-11 rounded-md border border-input bg-background px-1 text-center text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
                                  err?.min && 'border-red-500 focus-visible:ring-red-500',
                                )}
                              />
                              <input
                                type="text"
                                inputMode="numeric"
                                aria-label={`Docelowo: ${label}`}
                                data-testid={`req-${position.positionId}-${slot.id}-${day}-target`}
                                value={v.target}
                                disabled={!canEdit || saving}
                                onChange={(e) => setCell(k, 'target', e.target.value)}
                                aria-invalid={!!err?.target}
                                className={cn(
                                  'h-8 w-11 rounded-md border border-input bg-background px-1 text-center text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
                                  err?.target && 'border-red-500 focus-visible:ring-red-500',
                                )}
                              />
                            </div>
                            {(err?.min || err?.target) && (
                              <p className="mt-1 text-center text-[11px] leading-tight text-red-600 dark:text-red-400">
                                {err.min || err.target}
                              </p>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {canEdit && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {invalid && (
                  <span className="mr-auto text-sm text-red-600 dark:text-red-400">
                    Popraw zaznaczone pola, aby zapisać.
                  </span>
                )}
                {!invalid && dirty && (
                  <span className="mr-auto text-sm text-muted-foreground">Masz niezapisane zmiany.</span>
                )}
                <Button variant="outline" size="sm" onClick={() => setValues(initial)} disabled={!dirty || saving}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Przywróć
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!dirty || invalid || saving}
                  data-testid={`req-save-${position.positionId}`}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Zapisywanie...' : 'Zapisz'}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
